import React, { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import FormField from './FormField';
import { FORM_STATUS } from '../types';
import { exportToJSON } from '../hooks/useLocalStorage';
import { getMissingRequiredFields, isAnswerableField, isFieldValueFilled } from '../formUtils';

const parseInteger = (value) => {
  const parsed = Number.parseInt(value, 10);
  return Number.isInteger(parsed) ? parsed : null;
};

const getMetadataFieldName = (fieldName, suffix) => `_df_meta_${suffix}_${fieldName}`;

const getOrderedFields = (fields = []) => {
  return [...fields]
    .map((field, index) => ({
      ...field,
      __index: index,
    }))
    .sort((a, b) => {
      const orderA = parseInteger(a.order) ?? parseInteger(a.layout?.order);
      const orderB = parseInteger(b.order) ?? parseInteger(b.layout?.order);

      if (orderA !== null && orderB !== null && orderA !== orderB) {
        return orderA - orderB;
      }

      if (orderA !== null) {
        return -1;
      }

      if (orderB !== null) {
        return 1;
      }

      return a.__index - b.__index;
    })
    .map(({ __index, ...field }) => field);
};

const getFieldSpan = (field = {}) => {
  const configuredSpan = parseInteger(field.layout?.span) ?? parseInteger(field.span);
  const widthSpan = parseInteger(field.layout?.width);

  if (configuredSpan !== null || widthSpan !== null) {
    return Math.min(12, Math.max(1, configuredSpan ?? widthSpan));
  }

  if (field.type === 'textarea' || field.type === 'table' || field.type === 'checkbox-group' || field.type === 'label') {
    return 12;
  }

  return 6;
};

const FormFiller = ({
  form,
  template,
  client,
  onUpdate,
  onClose,
  onDelete,
  titleFocusRequest = 0,
  titleFocusFormId = null,
  readOnlyMessage = null,
}) => {
  const [formData, setFormData] = useState(form.data || {});
  const [formTitle, setFormTitle] = useState(form.title || '');
  const [status, setStatus] = useState(form.status || FORM_STATUS.DRAFT);
  const [showDataPanel, setShowDataPanel] = useState(false);
  const [saving, setSaving] = useState(false);
  const [printPreviewHtml, setPrintPreviewHtml] = useState('');
  const printStyleRef = useRef(null);

  const latestStateRef = useRef({
    data: form.data || {},
    title: form.title || '',
    status: form.status || FORM_STATUS.DRAFT,
  });
  const savingTimeoutRef = useRef(null);
  const shouldPersistOnUnmountRef = useRef(true);
  const currentFormIdRef = useRef(form.id);
  const persistCurrentStateRef = useRef(null);
  const titleInputRef = useRef(null);
  const lastTitleFocusRequestRef = useRef(titleFocusRequest);
  const isCompleted = status === FORM_STATUS.COMPLETED;
  const isReadOnly = isCompleted || Boolean(readOnlyMessage);

  const focusFormTitle = useCallback(() => {
    window.scrollTo({ top: 0, behavior: 'auto' });
    requestAnimationFrame(() => {
      titleInputRef.current?.focus();
    });
  }, []);

  useEffect(() => {
    const nextState = {
      data: form.data || {},
      title: form.title || '',
      status: form.status || FORM_STATUS.DRAFT,
    };

    if (currentFormIdRef.current !== form.id) {
      currentFormIdRef.current = form.id;
      shouldPersistOnUnmountRef.current = true;
      setFormData(nextState.data);
      setFormTitle(nextState.title);
      setStatus(nextState.status);
      latestStateRef.current = nextState;
    }
  }, [form]);

  useEffect(() => {
    latestStateRef.current = {
      data: formData,
      title: formTitle,
      status,
    };
  }, [formData, formTitle, status]);

  useEffect(() => {
    if (formTitle.trim()) {
      return;
    }

    focusFormTitle();
  }, [focusFormTitle, form.id, formTitle]);

  useEffect(() => {
    if (lastTitleFocusRequestRef.current === titleFocusRequest) {
      return;
    }

    lastTitleFocusRequestRef.current = titleFocusRequest;

    if (titleFocusFormId !== form.id) {
      return;
    }

    focusFormTitle();
  }, [focusFormTitle, form.id, titleFocusFormId, titleFocusRequest]);

  const flashSavingIndicator = useCallback(() => {
    setSaving(true);

    if (savingTimeoutRef.current) {
      clearTimeout(savingTimeoutRef.current);
    }

    savingTimeoutRef.current = setTimeout(() => setSaving(false), 500);
  }, []);

  useEffect(() => {
    return () => {
      if (savingTimeoutRef.current) {
        clearTimeout(savingTimeoutRef.current);
      }
    };
  }, []);

  const persistCurrentState = useCallback((overrides = {}, showIndicator = false) => {
    const payload = {
      data: latestStateRef.current.data,
      title: latestStateRef.current.title,
      status: latestStateRef.current.status,
      ...overrides,
    };

    latestStateRef.current = payload;
    if (readOnlyMessage) {
      return payload;
    }

    Promise.resolve(onUpdate(form.id, payload)).catch((error) => {
      console.error('Failed to persist form state:', error);
    });

    if (showIndicator) {
      flashSavingIndicator();
    }

    return payload;
  }, [flashSavingIndicator, form.id, onUpdate, readOnlyMessage]);

  useEffect(() => {
    persistCurrentStateRef.current = persistCurrentState;
  }, [persistCurrentState]);

  useEffect(() => {
    const interval = setInterval(() => {
      if (!readOnlyMessage) {
        persistCurrentStateRef.current?.({}, true);
      }
    }, 30000);

    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    const handleBeforeUnload = () => {
      persistCurrentStateRef.current?.();
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, []);

  useEffect(() => {
    return () => {
      if (shouldPersistOnUnmountRef.current) {
        persistCurrentStateRef.current?.();
      }
    };
  }, []);

  const handleFieldChange = (fieldName, value) => {
    if (isReadOnly) {
      return;
    }

    if (!fieldName) {
      return;
    }

    setFormData((previousData) => {
      const nextData = {
        ...previousData,
        [fieldName]: value,
      };

      latestStateRef.current = {
        ...latestStateRef.current,
        data: nextData,
      };

      return nextData;
    });
  };

  const handleFieldBlur = () => {
    if (isReadOnly) {
      return;
    }

    persistCurrentState({}, true);
  };

  const handleSave = () => {
    if (isReadOnly) {
      return;
    }

    persistCurrentState({}, true);
  };

  const handleComplete = () => {
    if (readOnlyMessage) {
      return;
    }

    const missingFields = getMissingRequiredFields(template, latestStateRef.current.data);
    const missingLabels = [
      ...(latestStateRef.current.title.trim() ? [] : ['Form Title']),
      ...missingFields.map((field) => field.label),
    ];

    if (missingLabels.length > 0) {
      alert(`Complete the required fields before marking this form complete:\n- ${missingLabels.join('\n- ')}`);

      if (!latestStateRef.current.title.trim()) {
        window.scrollTo({ top: 0, behavior: 'auto' });
        requestAnimationFrame(() => {
          titleInputRef.current?.focus();
        });
      }

      return;
    }

    if (window.confirm('Mark this form as completed? It will be locked from editing.')) {
      setStatus(FORM_STATUS.COMPLETED);
      persistCurrentState({ status: FORM_STATUS.COMPLETED }, true);
    }
  };

  const handleReopen = () => {
    if (readOnlyMessage) {
      return;
    }

    if (window.confirm('Reopen this completed form for editing?')) {
      setStatus(FORM_STATUS.DRAFT);
      persistCurrentState({ status: FORM_STATUS.DRAFT }, true);
    }
  };

  const handleClose = () => {
    shouldPersistOnUnmountRef.current = false;
    if (!readOnlyMessage) {
      persistCurrentState();
    }
    onClose();
  };

  const escapeHtml = (value) => String(value ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');

  const buildPrintableHtml = () => {
    const getFieldValue = (field, value) => {
      if (field.type === 'checkbox') {
        return value ? 'Yes' : 'No';
      }
      if (field.type === 'checkbox-group') {
        const selected = Array.isArray(value) ? value : [];
        const labels = field.options?.filter((o) => selected.includes(o.value)).map((o) => o.label) || [];
        return labels.length ? labels.join(', ') : '—';
      }
      if (field.type === 'select') {
        const opt = field.options?.find((o) => o.value === value);
        return opt?.label || value || '—';
      }
      if (field.type === 'table') {
        const rows = Array.isArray(value) ? value : [];
        if (!rows.length) return '—';
        const cols = field.columns || [];
        let tableHtml = '<table class="df-print-preview-table">';
        tableHtml += '<thead><tr>';
        cols.forEach((c) => {
          tableHtml += `<th>${escapeHtml(c.label)}</th>`;
        });
        tableHtml += '</tr></thead><tbody>';
        rows.forEach((row) => {
          tableHtml += '<tr>';
          cols.forEach((c) => {
            tableHtml += `<td>${escapeHtml(row[c.key] ?? '')}</td>`;
          });
          tableHtml += '</tr>';
        });
        tableHtml += '</tbody></table>';
        return tableHtml;
      }
      if (value === undefined || value === null || value === '') return '—';
      return String(value);
    };

    let html = `<div class="df-print-preview-document">
<div class="df-print-preview-doc-label">FQHC Dynamic Forms System</div>
<h1>${escapeHtml(formTitle || template.name)}</h1>
<div class="df-print-preview-summary">
  <div><strong>Template:</strong> ${escapeHtml(template.name)}</div>
  <div><strong>Status:</strong> ${isCompleted ? 'Completed' : 'Draft'}</div>
  ${client?.name ? `<div><strong>Client:</strong> ${escapeHtml(client.name)}</div>` : ''}
  ${client?.contactName ? `<div><strong>Contact:</strong> ${escapeHtml(client.contactName)}</div>` : ''}
  ${client?.location ? `<div><strong>Location:</strong> ${escapeHtml(client.location)}</div>` : ''}
</div>`;

    template.sections.forEach((section) => {
      const fields = getOrderedFields(section.fields);
      html += `<div class="df-print-preview-section">
<div class="df-print-preview-section-header"><div class="df-print-preview-section-title">${escapeHtml(section.icon || '')} ${escapeHtml(section.title)}</div></div>`;
      if (!fields.length) {
        html += '<div class="df-print-preview-empty">No fields in this section.</div>';
      }
      fields.forEach((field) => {
        if (field.type === 'label') {
          html += `<div class="df-print-preview-label-text">${escapeHtml(field.label)}</div>`;
          return;
        }
        const val = getFieldValue(field, formData[field.name]);
        const isTableHtml = typeof val === 'string' && val.startsWith('<table');
        const emptyValue = !formData[field.name] && formData[field.name] !== 0;
        html += `<div class="df-print-preview-field">
<div class="df-print-preview-field-label">${escapeHtml(field.label)}${field.required ? ' <span>*</span>' : ''}</div>
${isTableHtml ? val : `<div class="df-print-preview-field-value${emptyValue ? ' df-print-preview-field-value--empty' : ''}">${escapeHtml(val)}</div>`}
</div>`;
      });
      html += '</div>';
    });

    html += '</div>';
    return html;
  };

  const injectPrintStyle = () => {
    if (printStyleRef.current) return;
    const style = document.createElement('style');
    style.id = 'df-print-preview-style';
    style.textContent = `
      @media print {
        /* With the preview rendered through a body-level portal, hide every other body child. */
        body.df-print-preview-active > :not(.df-print-preview-overlay):not(script):not(style):not(link) {
          display: none !important;
        }
        .pro-sidebar,
        .pro-sidebar-inner,
        .sidebar-header,
        .sidebar-bottom,
        .navbar,
        .no-print,
        .no-print *,
        .df-print-preview-toolbar {
          display: none !important;
        }
        /* Show the overlay and its contents as normal print flow */
        .df-print-preview-overlay {
          display: block !important;
          position: static !important;
          inset: auto !important;
          width: auto !important;
          max-width: none !important;
          height: auto !important;
          max-height: none !important;
          overflow: visible !important;
          background: #ffffff !important;
          box-shadow: none !important;
          z-index: auto !important;
          padding: 0 !important;
          margin: 0 !important;
        }
        .df-print-preview-page {
          display: block !important;
          position: static !important;
          width: auto !important;
          max-width: none !important;
          height: auto !important;
          min-height: 0 !important;
          max-height: none !important;
          overflow: visible !important;
          margin: 0 !important;
          padding: 0.55in !important;
          background: #ffffff !important;
          box-shadow: none !important;
        }
        .df-print-preview-document {
          display: block !important;
          position: static !important;
          width: auto !important;
          max-width: none !important;
          height: auto !important;
          overflow: visible !important;
        }
        html,
        body,
        .df-print-preview-overlay,
        .df-print-preview-page,
        .df-print-preview-document {
          display: block !important;
          position: static !important;
          width: auto !important;
          min-width: 0 !important;
          max-width: none !important;
          height: auto !important;
          min-height: 0 !important;
          max-height: none !important;
          overflow: visible !important;
          contain: none !important;
          transform: none !important;
        }
        .df-print-preview-section,
        .df-print-preview-field,
        .df-print-preview-field-value,
        .df-print-preview-summary,
        .df-print-preview-table,
        .df-print-preview-table tbody,
        .df-print-preview-table tr,
        .df-print-preview-table td {
          break-inside: auto !important;
          page-break-inside: auto !important;
          overflow: visible !important;
        }
        .df-print-preview-section-header,
        .df-print-preview-document h1 {
          break-after: auto !important;
          page-break-after: auto !important;
        }
        @page {
          size: letter;
          margin: 0.55in;
        }
        html, body {
          background: #ffffff !important;
          color: #111111 !important;
          -webkit-print-color-adjust: exact;
          print-color-adjust: exact;
        }
      }
    `;
    document.head.appendChild(style);
    printStyleRef.current = style;
  };

  const removePrintStyle = () => {
    if (printStyleRef.current) {
      printStyleRef.current.remove();
      printStyleRef.current = null;
    }
  };

  const handlePrint = () => {
    document.body.classList.add('df-print-preview-active');
    injectPrintStyle();
    setPrintPreviewHtml(buildPrintableHtml());
    requestAnimationFrame(() => {
      window.scrollTo({ top: 0, behavior: 'auto' });
    });
  };

  const handlePrintPreviewPrint = () => {
    document.body.classList.add('df-print-preview-printing');
    window.print();
    setTimeout(() => {
      document.body.classList.remove('df-print-preview-printing');
    }, 500);
  };

  const handleClosePrintPreview = () => {
    document.body.classList.remove('df-print-preview-printing');
    document.body.classList.remove('df-print-preview-active');
    removePrintStyle();
    setPrintPreviewHtml('');
  };

  const handleExport = () => {
    const exportData = {
      form: {
        ...form,
        data: latestStateRef.current.data,
        title: latestStateRef.current.title,
        status: latestStateRef.current.status,
      },
      template,
      client,
      exportedAt: new Date().toISOString(),
    };

    exportToJSON(exportData, `form-${client?.name || 'unknown'}-${new Date().toISOString().split('T')[0]}.json`);
  };

  const calculateProgress = () => {
    if (!template?.sections) {
      return 0;
    }

    const fields = template.sections
      .flatMap((section) => section.fields || [])
      .filter((field) => isAnswerableField(field) && Boolean(field?.name));

    if (fields.length === 0) {
      return 0;
    }

    const filledFields = fields.filter((field) =>
      isFieldValueFilled(field, formData[field.name])
    ).length;

    return Math.round((filledFields / fields.length) * 100);
  };

  const progress = calculateProgress();

  if (!template) {
    return (
      <div className="card">
        <h2 className="card-title">Error</h2>
        <p>Template not found for this form.</p>
        <button className="btn btn-secondary" onClick={onClose}>Go Back</button>
      </div>
    );
  }

  return (
    <div className={`df-form-filler${printPreviewHtml ? ' df-form-filler--print-preview-open' : ''}`}>
      <nav className="navbar no-print" style={{ position: 'sticky', top: '60px', zIndex: 999 }}>
        <div className="navbar-content" style={{ justifyContent: 'flex-start', gap: '10px' }}>
          <button className="btn btn-secondary btn-sm" onClick={handleClose}>
            Back
          </button>
          <span style={{ color: 'white', marginLeft: '10px' }}>
            {client?.name} | {template.name}
          </span>
          <div style={{ marginLeft: 'auto', display: 'flex', gap: '10px', alignItems: 'center' }}>
            <span
              style={{
                color: isCompleted ? '#4caf50' : '#ffc107',
                fontWeight: 500,
              }}
            >
              {isCompleted ? 'Completed' : readOnlyMessage ? 'Shared read-only' : 'Draft'}
            </span>
            {saving && <span style={{ color: '#ccc', fontSize: '0.9rem' }}>Saving...</span>}
            {!isReadOnly && (
              <button className="btn btn-primary btn-sm" onClick={handleSave}>
                Save
              </button>
            )}
            <button className="btn btn-secondary btn-sm" onClick={handlePrint}>
              Print
            </button>
            <button className="btn btn-secondary btn-sm" onClick={handleExport}>
              Export JSON
            </button>
            {isCompleted ? (
              <button className="btn btn-primary btn-sm" onClick={handleReopen}>
                Reopen
              </button>
            ) : (
              <button className="btn btn-primary btn-sm" onClick={handleComplete}>
                Complete
              </button>
            )}
            {!isReadOnly && (
              <button
                className="btn btn-danger btn-sm"
                onClick={() => {
                  shouldPersistOnUnmountRef.current = false;
                  onDelete();
                }}
              >
                Delete
              </button>
            )}
          </div>
        </div>
      </nav>

      <div className="card no-print">
        <div className="form-group" style={{ margin: 0 }}>
          <label className="form-label">
            Form Title <span className="required">*</span>
          </label>
          <input
            ref={titleInputRef}
            type="text"
            className="form-input"
            value={formTitle}
            required
            readOnly={isReadOnly}
            onChange={(event) => {
              if (isReadOnly) {
                return;
              }

              const nextTitle = event.target.value;
              setFormTitle(nextTitle);
              latestStateRef.current = {
                ...latestStateRef.current,
                title: nextTitle,
              };
            }}
            placeholder={`${template.name} - ${client?.name || 'Untitled'}`}
            onBlur={() => {
              if (!isReadOnly) {
                persistCurrentState({}, true);
              }
            }}
          />
        </div>
      </div>

      {readOnlyMessage && (
        <div className="card no-print" style={{ borderLeft: '4px solid #ffc107' }}>
          <strong>{readOnlyMessage}</strong>
          <p style={{ margin: '8px 0 0' }}>
            You can review, print, or export this form, but editing is disabled while the public share link is active.
          </p>
        </div>
      )}

      {isCompleted && (
        <div className="card no-print" style={{ borderLeft: '4px solid #4caf50' }}>
          <strong>This form is completed and locked from editing.</strong>
          <p style={{ margin: '8px 0 0' }}>
            Use Reopen if changes are needed. Completed forms cannot be deleted from this screen.
          </p>
        </div>
      )}

      <div className="card no-print">
        <div className="df-progress-row">
          <span>Form Completion: {progress}%</span>
          <span>
            {progress < 30
              ? 'Getting Started'
              : progress < 70
                ? 'In Progress'
                : progress < 100
                  ? 'Almost Done'
                  : 'Complete'}
          </span>
        </div>
        <div className="df-progress-track">
          <div
            className="df-progress-fill"
            style={{ width: `${progress}%`, backgroundColor: progress === 100 ? '#4caf50' : '#00bcd4' }}
          />
        </div>
      </div>

      <div className="card df-print-card">
        <div className="print-only df-print-document-label">FQHC Dynamic Forms System</div>
        <h1 className="df-form-title">
          {formTitle || template.name}
        </h1>

        <div className="print-only df-print-summary">
          <div><strong>Template:</strong> {template.name}</div>
          <div><strong>Status:</strong> {isCompleted ? 'Completed' : 'Draft'}</div>
          {client?.name && <div><strong>Client:</strong> {client.name}</div>}
          {client?.contactName && <div><strong>Contact:</strong> {client.contactName}</div>}
          {client?.location && <div><strong>Location:</strong> {client.location}</div>}
        </div>

        {client && (
          <div className="quote-box df-screen-client-summary" style={{ marginBottom: '20px' }}>
            <strong>Client:</strong> {client.name}<br />
            {client.contactName && <><strong>Contact:</strong> {client.contactName}<br /></>}
            {client.location && <><strong>Location:</strong> {client.location}</>}
          </div>
        )}

        <nav className="navbar no-print" style={{ position: 'sticky', top: '108px', zIndex: 998, marginBottom: '20px' }}>
          <div className="navbar-content" style={{ flexWrap: 'wrap', height: 'auto', padding: '10px' }}>
            {template.sections.map((section) => (
              <a
                key={section.id}
                href={`#section-${section.id}`}
                className="nav-item"
                style={{ fontSize: '0.8rem', padding: '6px 12px' }}
              >
                {section.icon} {section.title}
              </a>
            ))}
          </div>
        </nav>

        <div className="df-form-sections">
          {template.sections.map((section) => {
            const orderedFields = getOrderedFields(section.fields);

            return (
              <section key={section.id} id={`section-${section.id}`} className="df-form-section" style={{ scrollMarginTop: '160px' }}>
                <div className="df-form-section-header">
                  <h2 className="df-form-section-title">
                    {section.icon} {section.title}
                  </h2>
                  <p className="df-form-section-subtitle">
                    {orderedFields.length} {orderedFields.length === 1 ? 'field' : 'fields'}
                  </p>
                </div>

                {orderedFields.length === 0 ? (
                  <div className="df-empty-state">No fields in this section.</div>
                ) : (
                  <div className="df-form-grid">
                    {orderedFields.map((field) => {
                      const baseFieldName = field.name || `field_${field.id}`;
                      const sourceFieldName = getMetadataFieldName(baseFieldName, 'source');
                      const noteFieldName = getMetadataFieldName(baseFieldName, 'note');
                      const sourceValue = formData[sourceFieldName] ?? '';
                      const noteValue = formData[noteFieldName] ?? '';
                      const fieldSpan = getFieldSpan(field);

                      return (
                        <article key={field.id} className={`df-form-field df-form-field--span-${fieldSpan}`}>
                          <FormField
                            field={field}
                            value={isAnswerableField(field) ? formData[field.name] : undefined}
                            onChange={handleFieldChange}
                            onBlur={handleFieldBlur}
                            readOnly={isReadOnly}
                          />
                          <details className="df-metadata-section df-field-metadata">
                            <summary>Source (interview reference) and Note</summary>
                            <div className="field-metadata">
                              <div className="form-group" style={{ marginTop: '12px', marginBottom: '12px' }}>
                                <label className="form-label">Source (interview reference)</label>
                                <input
                                  type="text"
                                  className="form-input"
                                  value={sourceValue}
                                  onChange={(event) => handleFieldChange(sourceFieldName, event.target.value)}
                                  onBlur={handleFieldBlur}
                                  readOnly={isReadOnly}
                                />
                              </div>
                              <div className="form-group" style={{ marginTop: '12px' }}>
                                <label className="form-label">Note</label>
                                <textarea
                                  className="form-textarea"
                                  rows={3}
                                  value={noteValue}
                                  onChange={(event) => handleFieldChange(noteFieldName, event.target.value)}
                                  onBlur={handleFieldBlur}
                                  readOnly={isReadOnly}
                                />
                              </div>
                            </div>
                          </details>
                        </article>
                      );
                    })}
                  </div>
                )}
              </section>
            );
          })}
        </div>

        <div className="action-buttons no-print" style={{ marginTop: '40px', textAlign: 'center' }}>
          {!isReadOnly && (
            <button className="btn btn-primary" onClick={handleSave}>
              Save Form
            </button>
          )}
          <button className="btn btn-secondary" onClick={handlePrint} style={{ marginLeft: '10px' }}>
            Print / Save as PDF
          </button>
          {isCompleted && !readOnlyMessage ? (
            <button className="btn btn-primary" onClick={handleReopen} style={{ marginLeft: '10px' }}>
              Reopen
            </button>
          ) : !readOnlyMessage ? (
            <button className="btn btn-primary" onClick={handleComplete} style={{ marginLeft: '10px' }}>
              Mark as Complete
            </button>
          ) : null}
          <button className="btn btn-secondary" onClick={handleClose} style={{ marginLeft: '10px' }}>
            Back to Dashboard
          </button>
        </div>
      </div>

      {printPreviewHtml && createPortal(
        <div className="df-print-preview-overlay">
          <div className="df-print-preview-toolbar no-print">
            <div>
              <strong>Print Preview</strong>
              <span>Review the simplified document, then print or save as PDF.</span>
            </div>
            <div className="df-print-preview-actions">
              <button className="btn btn-primary" onClick={handlePrintPreviewPrint}>
                Print / Save as PDF
              </button>
              <button className="btn btn-secondary" onClick={handleClosePrintPreview}>
                Back to Form
              </button>
            </div>
          </div>
          <div
            className="df-print-preview-page"
            dangerouslySetInnerHTML={{ __html: printPreviewHtml }}
          />
        </div>,
        document.body
      )}

      <div className="floating-panel no-print" style={{ display: showDataPanel ? 'block' : 'none' }}>
        <div className="floating-panel-header">
          <div className="floating-panel-title">Form Summary</div>
          <div className="floating-panel-controls">
            <button className="panel-btn" onClick={() => setShowDataPanel(false)} title="Close">x</button>
          </div>
        </div>
        <div className="floating-panel-content">
          <div className="form-group">
            <label><strong>Progress:</strong> {progress}% Complete</label>
          </div>
          <div className="form-group">
            <label><strong>Status:</strong></label>
            <div className={`badge badge-${status}`} style={{ marginTop: '5px' }}>
              {status}
            </div>
          </div>
          <div className="form-group">
            <label><strong>Sections:</strong> {template.sections.length}</label>
          </div>
          <div className="form-group">
            <label><strong>Fields:</strong> {template.sections.reduce((count, section) => count + section.fields.length, 0)}</label>
          </div>
          <div className="form-group">
            <label><strong>Last Saved:</strong></label>
            <div style={{ fontSize: '0.9rem', color: '#666' }}>
              {new Date().toLocaleTimeString()}
            </div>
          </div>
        </div>
      </div>

      {!showDataPanel && (
        <button
          className="no-print"
          style={{
            position: 'fixed',
            right: '20px',
            top: '140px',
            padding: '10px 15px',
            backgroundColor: 'var(--primary)',
            color: 'white',
            border: 'none',
            borderRadius: '5px',
            cursor: 'pointer',
            zIndex: 1000,
          }}
          onClick={() => setShowDataPanel(true)}
        >
          Show Summary
        </button>
      )}
    </div>
  );
};

export default FormFiller;


